/**
 * Canva Return Navigation Page
 * 
 * This page handles the return navigation from Canva after a user
 * completes their design work. Canva will redirect here with design
 * information in the URL parameters.
 */

import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export default function CanvaReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing return from Canva...');

  useEffect(() => {
    const handleReturn = async () => {
      try {
        // Get parameters from Canva
        const designId = searchParams.get('design_id');
        const designUrl = searchParams.get('design_url');
        const exportUrl = searchParams.get('export_url');
        const productId = searchParams.get('product_id');
        const productName = searchParams.get('product_name');

        if (!designId && !designUrl) {
          setStatus('error');
          setMessage('No design information received from Canva');
          toast.error('Invalid return from Canva');
          setTimeout(() => {
            navigate('/buyer/catalog');
          }, 3000);
          return;
        }

        setMessage('Saving design...');

        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setStatus('error');
          setMessage('Please log in to save designs');
          toast.error('Authentication required');
          setTimeout(() => {
            navigate('/auth?redirect=/buyer/catalog');
          }, 3000);
          return;
        }

        // Save design to database if we have export URL
        if (exportUrl && productId) {
          const { error } = await supabase
            .from('design_mockups')
            .insert({
              product_id: productId,
              buyer_id: user.id,
              logo_url: exportUrl,
              preview_3d_url: exportUrl,
              canva_design_id: designId,
              canva_design_url: designUrl || exportUrl,
              canva_export_url: exportUrl,
              design_metadata: {
                product_name: productName,
                created_via: 'canva_connect',
                returned_from_canva: true,
                returned_at: new Date().toISOString(),
              },
            });

          if (error) {
            logger.error('Error saving design', { error });
            // Don't fail completely, just log the error
          } else {
            toast.success('Design saved successfully!');
          }
        }

        setStatus('success');
        setMessage('Successfully returned from Canva!');

        // Redirect based on context
        setTimeout(() => {
          if (productId) {
            navigate(`/buyer/catalog/${productId}`);
          } else {
            navigate('/buyer/catalog');
          }
        }, 2000);

      } catch (error) {
        logger.error('Return navigation error', { error });
        setStatus('error');
        setMessage('An error occurred while processing the return');
        toast.error('Failed to process return from Canva');
        
        setTimeout(() => {
          navigate('/buyer/canva-editor');
        }, 3000);
      }
    };

    handleReturn();
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Returning from Canva</CardTitle>
          <CardDescription>Processing your design...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'loading' && (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="size-12 animate-spin text-primary" />
              <p className="text-center text-muted-foreground">{message}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center gap-4">
              <CheckCircle2 className="size-12 text-success" />
              <p className="text-center font-medium">{message}</p>
              <p className="text-center text-sm text-muted-foreground">Redirecting...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center gap-4">
              <p className="text-center text-destructive">{message}</p>
              <Button
                variant="outline"
                onClick={() => navigate('/buyer/catalog')}
                className="w-full"
              >
                <ArrowLeft className="mr-2 size-4" />
                Back to Catalog
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

